import { useEffect, useRef, type ReactNode } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'

type Tone = 'neutral' | 'success' | 'alert'

const toneStyles: Record<Tone, string> = {
  neutral: 'bg-muted text-muted-foreground',
  success: 'bg-primary/10 text-primary',
  alert: 'bg-destructive/10 text-destructive',
}

/** Centered single-message screen used for every terminal state (done / expired / locked / error). */
export function StatusCard({
  tone = 'neutral',
  icon,
  title,
  children,
  action,
}: {
  tone?: Tone
  icon: ReactNode
  title: string
  children?: ReactNode
  action?: ReactNode
}) {
  const headingRef = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
        <div className={cn('flex size-14 items-center justify-center rounded-full', toneStyles[tone])}>
          {icon}
        </div>
        <h1 ref={headingRef} tabIndex={-1} className="text-xl font-semibold tracking-tight outline-none">
          {title}
        </h1>
        {children && (
          <p className="text-muted-foreground max-w-xs text-sm leading-relaxed">{children}</p>
        )}
        {action}
      </CardContent>
    </Card>
  )
}
